import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { cn } from "@/core/utils/utils";
import {
  SELECTION_AGGREGATE_LABELS,
  type SelectionAggregate,
  type SelectionSummary,
} from "./selection-summary";
import { VIEWER_OVERLAY_CLASSNAME } from "./utils";

interface SelectionSummaryControlProps {
  summary: SelectionSummary;
}

export const SelectionSummaryControl = ({
  summary,
}: SelectionSummaryControlProps) => {
  const [selectedAggregate, setSelectedAggregate] =
    useState<SelectionAggregate | null>(null);
  const selectedOption = summary.options.find(
    (option) => option.aggregate === selectedAggregate,
  );
  const activeOption = selectedOption ?? summary.defaultOption;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Selection summary: ${SELECTION_AGGREGATE_LABELS[activeOption.aggregate]}`}
          className="mr-6 flex h-full shrink-0 items-center gap-1.5 border-l border-stroke-tertiary px-3 text-md text-secondary-text transition-colors hover:bg-surface-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          <span className="font-medium">
            {SELECTION_AGGREGATE_LABELS[activeOption.aggregate]}:
          </span>
          <span>{activeOption.result}</span>
          <ChevronDown className="h-3.5 w-3.5 text-tertiary-text" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(VIEWER_OVERLAY_CLASSNAME, "min-w-48")}
      >
        {summary.options.map((option) => (
          <DropdownMenuItem
            key={option.aggregate}
            className="justify-between gap-4"
            onSelect={() => setSelectedAggregate(option.aggregate)}
          >
            {SELECTION_AGGREGATE_LABELS[option.aggregate]}
            <span className="flex items-center gap-2">
              <span className="tabular-nums text-tertiary-text">
                {option.result}
              </span>
              <Check
                className={cn("h-4 w-4", {
                  invisible: option.aggregate !== activeOption.aggregate,
                })}
              />
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
